/**
 * Environment Detection for Agent Squad Streaming
 *
 * Simple environment detection following Agent Squad specification
 */

export interface EnvironmentConfig {
  name: string;
  apiEndpoint: string;
  cloudFrontDomain?: string;
  cognito?: {
    region: string;
    userPoolId: string;
    userPoolClientId: string;
    identityPoolId: string;
    domain: string;
  };
}

// Environment configurations - simplified to match Agent Squad spec
const ENVIRONMENT_CONFIGS: Record<string, EnvironmentConfig> = {
  local: {
    name: 'Local Development (Litestar)',
    apiEndpoint: 'http://localhost:8000',
  },
  cognito: {
    name: 'Local Development with Cognito',
    apiEndpoint: 'http://localhost:8000',
    cognito: {
      region: 'ap-southeast-2',
      userPoolId: 'ap-southeast-2_UJ6oN5enz',
      userPoolClientId: '2i4h6k8qlkfn3rm641o1nh4ka',
      identityPoolId: 'ap-southeast-2:d54ff157-2e45-47c9-9e04-003453d157b1',
      domain: 'dev-agile-auth.auth.ap-southeast-2.amazoncognito.com',
    },
  },
  'dev-v2': {
    name: 'Agent Squad Development V2 (Direct Function URL)',
    apiEndpoint: 'https://dev-v2.api.ada.allianzretireplus.com.au',
    cloudFrontDomain: 'd31q1f7vmjzls4.cloudfront.net',
    cognito: {
      region: 'ap-southeast-2',
      userPoolId: 'ap-southeast-2_UJ6oN5enz',
      userPoolClientId: '2i4h6k8qlkfn3rm641o1nh4ka',
      identityPoolId: 'ap-southeast-2:d54ff157-2e45-47c9-9e04-003453d157b1',
      domain: 'dev-agile-auth.auth.ap-southeast-2.amazoncognito.com',
    },
  },
  'prod-v2': {
    name: 'Agent Squad Production V2 (Direct Function URL)',
    apiEndpoint: 'https://api.ada.allianzretireplus.com.au',
    cloudFrontDomain: 'dr1xcewiyqnnf.cloudfront.net',
    cognito: {
      region: 'ap-southeast-2',
      userPoolId: 'ap-southeast-2_YozwuU4X8',
      userPoolClientId: 'imsp4snv9tccd73o4hf660ouo',
      identityPoolId: 'ap-southeast-2:0c2e4345-d84a-4a7a-accd-28bb56ec9167',
      domain: 'prod-agile-auth.auth.ap-southeast-2.amazoncognito.com',
    },
  },
};

export function detectEnvironment(): string {
  const hostname = window.location.hostname;

  // Check for localhost development
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.')
  ) {
    // Check URL parameters for backend mode (convenient for development)
    const urlParams = new URLSearchParams(window.location.search);
    console.log('URL Params:', urlParams.toString());
    if (
      urlParams.get('cognito') === 'true' ||
      localStorage.getItem('USE_COGNITO') === 'true'
    ) {
      localStorage.setItem('USE_COGNITO', 'true');
      return 'cognito';
    }
    if (urlParams.get('backend') === 'mock') {
      localStorage.removeItem('USE_COGNITO');
      return 'local';
    }

    // Default localhost to mock API
    return 'local';
  }

  // Check for Production V2 CloudFront domain (when provisioned)
  if (hostname === 'ada.allianzretireplus.com.au') {
    return 'prod-v2';
  }

  // Default to dev-v2 for all other cases
  return 'dev-v2';
}

/**
 * Get configuration for current environment
 */
export function getCurrentEnvironmentConfig(): EnvironmentConfig {
  const environment = detectEnvironment();
  return ENVIRONMENT_CONFIGS[environment];
}

/**
 * Get Function URL endpoint for Agent Squad
 */
export function getStreamingEndpoint(): string | null {
  const config = getCurrentEnvironmentConfig();
  return config.apiEndpoint || null;
}
